import { get, range, uniqBy } from 'lodash';
import * as React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useInfiniteQuery } from 'react-query';
import styled from 'styled-components';
import { SpokeLoading } from '@doltech/core/lib/auth/SpokeLoading';

const Main = styled.div`
  .seemore-container {
    display: grid;
    place-items: center;
    grid-template-columns: minmax(0, 1fr);
  }
`;

interface PostListingProps {
  ssrData?: any;
  apiKey: string[];
  apiFn: (params?: any) => Promise<any>;
  pageSize: number;
  infiniteScrollEnabled?: boolean;
  renderItem: (item: any, index: number) => JSX.Element;
  renderSkeleton: (item: any, index: number) => JSX.Element;
  renderSeparator?: (item: any, index: number) => JSX.Element;
  className?: string;
  renderLoadMore?: ({ fetchNextPage }: any) => JSX.Element;
  haveSeparatorAtEnd?: boolean;
  renderHeader?: ({ data }: any) => JSX.Element;

  beFrameworkName?: 'spring' | 'micronaut';
  useWindow?: boolean;
  getScrollParent?: () => any;
  keyIdentifier?: string;

  renderEmpty?: () => JSX.Element;
}

export const PostListing = (props: PostListingProps) => {
  const {
    ssrData,
    apiFn,
    apiKey,
    pageSize,
    infiniteScrollEnabled = false,
    renderItem = () => <div />,
    renderSkeleton = () => <div />,
    renderEmpty,
    renderSeparator,
    className,
    renderHeader = () => null,
    renderLoadMore = () => null,
    haveSeparatorAtEnd = false,
    keyIdentifier = 'id',

    beFrameworkName = 'spring',
    ...rest
  } = props;
  const { status, isFetchingNextPage, data, fetchNextPage, hasNextPage } = useInfiniteQuery(
    apiKey,
    (nextParams) => {
      const { pageParam } = nextParams;
      return apiFn({
        size: pageSize,
        page: pageParam,
      });
    },
    {
      retry: false,
      initialData: ssrData
        ? {
            pages: [ssrData],
            pageParams: [0],
          }
        : undefined,
      staleTime: 500,
      enabled: infiniteScrollEnabled,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return undefined;
        if (beFrameworkName === 'micronaut') {
          if (lastPage.empty) return undefined;
          const { number } = lastPage.pageable;
          if (number > lastPage?.totalPages || lastPage?.totalPages === 1) return undefined;
          return number + 1;
        }
        const { number, last } = lastPage;
        if (last || !infiniteScrollEnabled) {
          return undefined;
        }
        return number + 1;
      },
    }
  );

  const listData =
    get(data, 'content') ||
    get(data, 'pages', []).reduce((p, v) => p.concat(v?.content || []), []) ||
    [];
  const isEmpty = status === 'loading' || listData?.length === 0;
  const list = uniqBy(listData, keyIdentifier);

  const renderContent = React.useCallback(() => {
    return (
      <InfiniteScroll
        className={className}
        pageStart={-1}
        useWindow
        hasMore={hasNextPage}
        loadMore={() => {
          if (renderLoadMore({})) return;
          fetchNextPage();
        }}
        {...rest}
      >
        {list.map((item: any, index) => {
          return (
            <React.Fragment key={item?.[keyIdentifier] || index}>
              {renderItem(item, index)}
              {renderSeparator &&
                (haveSeparatorAtEnd || index < list.length - 1) &&
                renderSeparator(item, index)}
            </React.Fragment>
          );
        })}
        {isFetchingNextPage && range(pageSize).map(renderSkeleton)}
        {hasNextPage && renderLoadMore({}) && (
          <div className="seemore-container">{renderLoadMore({ fetchNextPage })}</div>
        )}
      </InfiniteScroll>
    );
  }, [
    className,
    fetchNextPage,
    hasNextPage,
    haveSeparatorAtEnd,
    isFetchingNextPage,
    keyIdentifier,
    list,
    pageSize,
    renderItem,
    renderLoadMore,
    renderSeparator,
    renderSkeleton,
    rest,
  ]);

  if (status === 'loading') {
    return <SpokeLoading />;
  }
  if (isEmpty) {
    if (renderEmpty) {
      return renderEmpty();
    }
    return null;
  }

  return (
    <Main className="post-listing">
      {renderHeader({ data: data?.pages?.[0] })}
      {renderContent()}
    </Main>
  );
};
